/**
 * LinkedIn Sidebar Component
 *
 * LEFT panel showing list of LinkedIn connections.
 * Includes search bar and tabs for filtering.
 */

import React, { useState } from "react";
import { Icons } from "../../../../../../views/crm-pages/conversation/constant";
import { LinkedInSidebarProps } from "./types";
import { ConnectionCard } from "./connection-card";

export const LinkedInSidebar: React.FC<LinkedInSidebarProps> = ({
  connections,
  selectedId,
  onSelect,
  searchQuery,
  onSearchChange,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const filteredConnections = connections.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.company.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-[260px] min-w-[200px] bg-white dark:bg-gray-900 border-r border-[#e8ecf4] dark:border-gray-800 flex flex-col h-full overflow-hidden">
      {/* Search Header */}
      <div className="p-3 pb-2 border-b border-slate-100 dark:border-gray-800 bg-[#EAEDFB] dark:bg-gray-800/60">
        <div className="flex items-center gap-2 bg-slate-50 dark:bg-gray-800 rounded-lg p-2 border border-slate-200 dark:border-gray-700">
          <Icons.Search />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search Conversations"
            className="border-none bg-transparent outline-none text-xs text-slate-600 dark:text-gray-300 w-full placeholder:text-slate-400 dark:placeholder:text-gray-500"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-100 dark:border-gray-800">
        {["All", "Unread", "Groups"].map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors ${
              activeTab === i
                ? "text-[#1C6E46] dark:text-emerald-400 border-b-2 border-[#1C6E46] dark:border-emerald-400"
                : "text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Connection List */}
      <div className="flex-1 overflow-y-auto">
        {filteredConnections.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-6 text-center">
            <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-gray-800 flex items-center justify-center mb-3 text-slate-400 dark:text-gray-500">
              <Icons.Linkedin />
            </div>
            <p className="text-sm font-medium text-slate-600 dark:text-gray-300">No conversations found</p>
            <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">Try a different search term</p>
          </div>
        ) : (
          filteredConnections.map((connection) => (
            <ConnectionCard
              key={connection.id}
              connection={connection}
              isSelected={selectedId === connection.id}
              onClick={() => onSelect(connection)}
            />
          ))
        )}
      </div>
    </div>
  );
};
